import { KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import { CustomIcon } from "components/icon/customIcon.component";
import {
  V2_INPUT_HEIGHT_PX,
  V2_INPUT_WIDTH_PX,
} from "components/designV2/constants";

export type DropdownOption = {
  value: string;
  label: string;
};

/** Props for the dropdown component. */
export interface DropdownProps {
  label?: string;
  options: DropdownOption[];
  value?: string;
  placeholder?: string;
  disabled?: boolean;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
}

const DEFAULT_MAX_HEIGHT = 260;

/** Dropdown input with selectable options. */
export const Dropdown = ({
  label,
  options,
  value,
  placeholder,
  disabled,
  onChange,
  onBlur,
  onFocus,
}: DropdownProps): JSX.Element => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const placeholderText = placeholder ?? t("General.SelectOption");

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        onBlur?.();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onBlur]);

  const handleToggle = (): void => {
    if (disabled) return;
    setIsOpen((prev) => {
      const next = !prev;
      if (next) onFocus?.();
      if (!next) onBlur?.();
      return next;
    });
  };

  const handleSelect = (selectedValue: string): void => {
    if (disabled) return;
    onChange?.(selectedValue);
    setIsOpen(false);
    onBlur?.();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (disabled) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleToggle();
    } else if (event.key === "Escape") {
      setIsOpen(false);
      onBlur?.();
    }
  };

  return (
    <Container ref={dropdownRef} aria-disabled={disabled}>
      {label && <Label>{label}</Label>}
      <Trigger
        role="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        tabIndex={disabled ? -1 : 0}
        $isOpen={isOpen}
        $disabled={disabled}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
      >
        {selectedOption ? (
          <ValueText>{selectedOption.label}</ValueText>
        ) : (
          <Placeholder>{placeholderText}</Placeholder>
        )}
        <IconWrapper>
          <CustomIcon
            name={isOpen ? "v2/up-chevron" : "v2/down-chevron"}
            size="24px"
            color={ColorV2.Blue5}
          />
        </IconWrapper>
      </Trigger>
      <List role="listbox" $isOpen={isOpen}>
        {options.map((option) => {
          const isSelected = option.value === value;
          return (
            <Option
              key={option.value}
              role="option"
              aria-selected={isSelected}
              $isSelected={isSelected}
              onClick={() => handleSelect(option.value)}
            >
              {option.label}
            </Option>
          );
        })}
      </List>
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  width: ${V2_INPUT_WIDTH_PX}px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.span`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue4};
`;

const Trigger = styled.div<{ $disabled?: boolean; $isOpen?: boolean }>`
  height: ${V2_INPUT_HEIGHT_PX}px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  border-radius: 16px;
  background-color: ${ColorV2.White};
  border: 1.5px solid ${ColorV2.Blue5};
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  ${(props) =>
    props.$isOpen &&
    css`
      box-shadow: 0 6px 16px rgba(15, 42, 64, 0.12);
      border-color: ${ColorV2.Blue5};
    `}

  ${(props) =>
    props.$disabled &&
    css`
      opacity: 0.6;
      cursor: not-allowed;
      pointer-events: none;
    `}
`;

const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ValueText = styled.span`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue1};
`;

const Placeholder = styled.span`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue5};
`;

const List = styled.ul<{ $isOpen?: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin: 4px 0 0 0;
  padding: 8px 0;
  list-style: none;
  border-radius: 16px;
  background-color: ${ColorV2.White};
  border: 1.5px solid ${ColorV2.Blue5};
  box-shadow: 0 12px 24px rgba(15, 42, 64, 0.12);
  max-height: ${DEFAULT_MAX_HEIGHT}px;
  overflow-y: auto;
  z-index: 10;
  display: ${(props) => (props.$isOpen ? "block" : "none")};
`;

const Option = styled.li<{ $isSelected?: boolean }>`
  padding: 12px 16px;
  margin: 4px 8px;
  border-radius: 12px;
  ${TypographyV2.b2}
  color: ${ColorV2.Blue1};
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;

  &:hover {
    background-color: ${ColorV2.Blue6};
    color: ${ColorV2.Blue1};
  }

  ${(props) =>
    props.$isSelected &&
    css`
      background-color: ${ColorV2.Blue1};
      color: ${ColorV2.White};
    `}
`;
